/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  type AutoUpdateOptions,
  type ElementProps,
  flip,
  offset,
  type Placement,
  type ReferenceType,
  safePolygon,
  shift,
  useClick,
  useClientPoint,
  useDismiss,
  useFloating,
  type UseFloatingOptions,
  type UseFloatingReturn,
  useFocus,
  useHover,
  useInteractions,
  type UseInteractionsReturn,
  useRole,
} from '@floating-ui/react';
import {
  createContext,
  type PointerEvent as ReactPointerEvent,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface TooltipOptions {
  isInitialOpen?: boolean;
  placement?: Placement;
  isOpen?: boolean;
  onOpenChange?: UseFloatingOptions['onOpenChange'];
  type?: 'simple' | 'rich';
  isPortaled?: boolean;
  strategy?: 'fixed' | 'absolute';
  hoverDelay?: {
    open: number;
    close: number;
  };
  shouldCloseOnReferenceClick?: boolean;
  autoUpdateOptions?: AutoUpdateOptions;
  isDisabled?: boolean;
  // TODO v5: align followCursor with the Tippy.js convention: `true` should follow
  // the cursor continuously on both axes, with explicit 'horizontal' / 'vertical'
  // values for single-axis following (instead of deriving the axis from `placement`).
  /**
   * Whether the tooltip should be positioned at the mouse cursor. Only applies to 'simple' tooltips with mouse hover.
   * `true` follows the cursor continuously along one axis derived from `placement` (horizontal for top/bottom, vertical for left/right).
   * `'initial'` opens the tooltip at the cursor's position (both axes) and keeps it there, like the native `title` attribute; `placement` controls which side of that point the tooltip appears on.
   */
  followCursor?: boolean | 'initial';
}

export function useTooltip({
  isInitialOpen = false,
  placement = 'top',
  isOpen: controlledOpen,
  onOpenChange: setControlledOpen,
  type = 'simple',
  isPortaled = true,
  strategy = 'absolute',
  hoverDelay = undefined,
  shouldCloseOnReferenceClick = false,
  autoUpdateOptions,
  isDisabled = false,
  followCursor = false,
}: TooltipOptions = {}): {
  headerId?: string;
  setHeaderId?: (id: string | undefined) => void;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  type: 'simple' | 'rich';
  isPortaled: boolean;
} & UseInteractionsReturn &
  UseFloatingReturn<ReferenceType> {
  const id = useId();
  const [headerId, setHeaderId] = useState<string | undefined>(id);
  const [isUncontrolledOpen, setIsUncontrolledOpen] = useState(isInitialOpen);
  const isOpen = controlledOpen ?? isUncontrolledOpen;
  const setOpen: NonNullable<UseFloatingOptions['onOpenChange']> =
    setControlledOpen ?? setIsUncontrolledOpen;

  // For followCursor='initial': the latest pointer position over the trigger,
  // and a snapshot of it frozen at the moment the tooltip opens.
  const pointerPositionRef = useRef<{
    // oxlint-disable-next-line id-length
    x: number;
    // oxlint-disable-next-line id-length
    y: number;
  } | null>(null);
  const [initialCursorPoint, setInitialCursorPoint] = useState<{
    // oxlint-disable-next-line id-length
    x: number;
    // oxlint-disable-next-line id-length
    y: number;
  } | null>(null);

  const data = useFloating({
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
    onOpenChange(open, event, reason) {
      if (followCursor === 'initial') {
        // Only anchor to the cursor for pointer-driven opens; keyboard focus
        // opens fall back to positioning relative to the trigger element.
        const isPointerDrivenOpen = event !== undefined && 'clientX' in event;
        setInitialCursorPoint(
          open && isPointerDrivenOpen ? pointerPositionRef.current : null,
        );
      }
      setOpen(open, event, reason);
    },
    open: isOpen,
    placement,
    strategy,
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        ...autoUpdateOptions,
      });
      return cleanup;
    },
  });

  const context = data.context;

  const hover = useHover(context, {
    delay: hoverDelay,
    enabled: type === 'simple' && !isDisabled,
    handleClose: safePolygon(),
    move: false,
  });
  const click = useClick(context, {
    enabled: type === 'rich' && !isDisabled,
  });
  const focus = useFocus(context, {
    enabled: type === 'simple' && !isDisabled,
    visibleOnly: true,
  });
  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
    referencePress: shouldCloseOnReferenceClick,
  });
  const role = useRole(context, {
    role: type === 'simple' ? 'tooltip' : 'dialog',
  });
  const followAxis =
    placement.startsWith('left') || placement.startsWith('right') ? 'y' : 'x';
  const isInitialFollowCursor = followCursor === 'initial';
  const clientPoint = useClientPoint(context, {
    // 'initial' anchors to the exact cursor point (both axes); `true` follows
    // continuously along the axis derived from `placement`.
    axis: isInitialFollowCursor ? 'both' : followAxis,
    enabled: followCursor !== false && type === 'simple' && !isDisabled,
    // Explicit coordinates lock the position: useClientPoint stops tracking
    // pointer movement when they are provided.
    ...(isInitialFollowCursor && {
      // oxlint-disable-next-line id-length
      x: initialCursorPoint?.x ?? null,
      // oxlint-disable-next-line id-length
      y: initialCursorPoint?.y ?? null,
    }),
  });

  const initialCursorTracking = useMemo<ElementProps>(() => {
    if (!isInitialFollowCursor) {
      return {};
    }
    const capturePointerPosition = (event: ReactPointerEvent) => {
      pointerPositionRef.current = {
        // oxlint-disable-next-line id-length
        x: event.clientX,
        // oxlint-disable-next-line id-length
        y: event.clientY,
      };
    };
    return {
      reference: {
        onPointerEnter: capturePointerPosition,
        onPointerMove: capturePointerPosition,
      },
    };
  }, [isInitialFollowCursor]);

  const interactions = useInteractions([
    hover,
    focus,
    dismiss,
    role,
    click,
    clientPoint,
    initialCursorTracking,
  ]);

  return useMemo(
    () => ({
      headerId: type === 'rich' ? headerId : undefined,
      isOpen,
      isPortaled,
      setHeaderId: type === 'rich' ? setHeaderId : undefined,
      setOpen,
      type,
      ...interactions,
      ...data,
    }),
    [headerId, isOpen, isPortaled, setOpen, type, interactions, data],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

export const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = (): ReturnType<typeof useTooltip> => {
  const context = useContext(TooltipContext);

  if (context === null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};
